import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const I18nContext = createContext({ t: (s) => s, lang: "en", setLang: () => {}, languages: [] });
const LANG_KEY = "e650_lang";

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const [dict, setDict] = useState({});
  const [languages, setLanguages] = useState([]);
  const appliedForUser = useRef(null);

  useEffect(() => {
    api.get("/translations").then((r) => setLanguages(r.data || [])).catch(() => setLanguages([]));
  }, []);

  useEffect(() => {
    if (lang === "en") { setDict({}); return; }
    api.get(`/translations/${lang}`)
      .then((r) => setDict(r.data?.data || {}))
      .catch(() => setDict({}));
  }, [lang]);

  // On login, adopt the language saved on the profile — follows the student across devices.
  useEffect(() => {
    if (!user) { appliedForUser.current = null; return; }
    if (appliedForUser.current === user.id) return;
    appliedForUser.current = user.id;
    const pref = user.language_pref;
    if (pref && pref !== lang) { localStorage.setItem(LANG_KEY, pref); setLangState(pref); }
    // eslint-disable-next-line
  }, [user]);

  const setLang = (code) => {
    const prev = lang;
    localStorage.setItem(LANG_KEY, code);
    setLangState(code);
    api.post("/auth/me/language", { lang: code })
      .catch(() => {})
      .finally(() => {
        // Leaving a translated language: reload so every rewritten DOM node returns to English.
        if (prev !== "en" && code === "en") window.location.reload();
      });
  };
  // Keys are the English source strings — anything untranslated falls back to English.
  const t = useCallback((s) => (dict && dict[s]) || s, [dict]);

  // ---- Full-page translation layer ----
  // Guarantees a 100% French experience: every rendered text node, placeholder and
  // tooltip whose English text is in the dictionary gets replaced — even in screens
  // that don't call t() themselves. Exact-match only, so no mistranslation risk.
  const dictRef = useRef(dict);
  dictRef.current = dict;
  useEffect(() => {
    if (lang === "en" || !dict || !Object.keys(dict).length) return undefined;
    const ATTRS = ["placeholder", "title", "aria-label"];
    // Pattern rules for strings that embed live numbers/dates (exact match can't catch them).
    const MOIS = { January: "janvier", February: "février", March: "mars", April: "avril", May: "mai", June: "juin", July: "juillet", August: "août", September: "septembre", October: "octobre", November: "novembre", December: "décembre" };
    const NIV = { Bronze: "Bronze", Silver: "Argent", Gold: "Or", Platinum: "Platine", Legend: "Légende" };
    const RX = [
      [/^Need ([\d,]+) coins \(you have ([\d,]+)\)$/, (m) => `Il te faut ${m[1]} pièces (tu en as ${m[2]})`],
      [/^Attempt (\d+)\/(\d+)$/, (m) => `Tentative ${m[1]}/${m[2]}`],
      [/^(\d+) lessons? done today\.$/, (m) => `${m[1]} leçon${m[1] > 1 ? "s" : ""} terminée${m[1] > 1 ? "s" : ""} aujourd'hui.`],
      [/^(Bronze|Silver|Gold|Platinum|Legend) #(\d+)$/, (m) => `${NIV[m[1]]} nº${m[2]}`],
      [/^(Bronze|Silver|Gold|Platinum|Legend) — (\d+)% of the way to (.+) \(([\d,]+) lifetime coins\)$/, (m) => `${NIV[m[1]]} — ${m[2]} % du chemin vers ${m[3]} (${m[4]} pièces à vie)`],
      [/^Emeralds — (\d+) coins each$/, (m) => `Émeraudes — ${m[1]} pièces chacune`],
      [/^~(.+) total · ?$/, (m) => `~${m[1]} au total · `],
      [/^~ ?(.+) left$/, (m) => `~${m[1]} restantes`],
      [/^~(.+) total · ~(.+) left$/, (m) => `~${m[1]} au total · ~${m[2]} restantes`],
      [/^(\d+\. .*) — done ✓$/, (m) => `${m[1]} — terminée ✓`],
      [/^Unlock for (\d+) emeralds?$/, (m) => `Débloquer pour ${m[1]} émeraude${m[1] > 1 ? "s" : ""}`],
      [/^\((\d+)\/(\d+) held\)$/, (m) => `(${m[1]}/${m[2]} possédés)`],
      [/^(\d+) emeralds?$/, (m) => `${m[1]} émeraude${m[1] > 1 ? "s" : ""}`],
      [/^(\d+)-day best streak$/, (m) => `meilleure série : ${m[1]} jours`],
      [/^Up (\d+) vs the month before$/, (m) => `En hausse de ${m[1]} par rapport au mois précédent`],
      [/^(January|February|March|April|May|June|July|August|September|October|November|December) (\d{4})$/, (m) => `${MOIS[m[1]]} ${m[2]}`],
      [/^(.+): (\d+) of (\d+) lessons done \((\d+)%\)$/, (m) => `${(dictRef.current || {})[m[1]] || m[1]} : ${m[2]} leçon${m[3] > 1 ? "s" : ""} sur ${m[3]} terminées (${m[4]} %)`],
    ];
    const lookup = (s) => {
      const d = dictRef.current;
      if (!d) return null;
      if (d[s] !== undefined) return d[s];
      const trimmed = s.trim();
      if (trimmed && d[trimmed] !== undefined) return s.replace(trimmed, d[trimmed]);
      for (const [rx, fn] of RX) {
        const m = trimmed.match(rx);
        if (m) return s.replace(trimmed, fn(m));
      }
      return null;
    };
    const translateNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const v = node.nodeValue;
        if (v && v.trim().length > 1) {
          const out = lookup(v);
          if (out !== null && out !== v) node.nodeValue = out;
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName;
      if (tag === "SCRIPT" || tag === "STYLE") return;
      ATTRS.forEach((a) => {
        const v = node.getAttribute && node.getAttribute(a);
        if (v) {
          const out = lookup(v);
          if (out !== null && out !== v) node.setAttribute(a, out);
        }
      });
      // Textareas: translate the placeholder (above) but never touch their typed content.
      if (tag === "TEXTAREA") return;
      let child = node.firstChild;
      while (child) { translateNode(child); child = child.nextSibling; }
    };
    let scheduled = false;
    let pending = [];
    const sweep = () => {
      scheduled = false;
      const batch = pending;
      pending = [];
      obs.disconnect();
      if (batch.length) {
        batch.forEach((r) => {
          if (r.type === "characterData" || r.type === "attributes") translateNode(r.target);
          r.addedNodes && r.addedNodes.forEach(translateNode);
        });
      } else {
        translateNode(document.body);
      }
      observe();
    };
    const schedule = (records) => {
      if (records) pending.push(...records);
      if (!scheduled) { scheduled = true; requestAnimationFrame(sweep); }
    };
    const obs = new MutationObserver(schedule);
    const observe = () => obs.observe(document.body, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ATTRS,
    });
    translateNode(document.body);
    observe();
    return () => obs.disconnect();
  }, [lang, dict]);

  return <I18nContext.Provider value={{ t, lang, setLang, languages }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
